import { format, subMinutes } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, DATE_FORMATS_FNS, UploadFilesZone, UploadedFilesPreview } from "components";
import { LogHistory, SnoozeForm } from "components/SnoozeModal/components";
import "components/SnoozeModal/SnoozeModal.scss";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, SNOOZE_STATUSES } from "models";
import { useAppointmentForSnoozeQuery } from "modules/AppointmentDetails/hooks";
import { getFilesWithinSizeLimit } from "utils/common";
import { formatCurrentUTCDate, formatDate } from "utils/dateHelperFunctions";

import type { SnoozeFormValues } from "components/SnoozeModal/components";
import type { Dealer, DealerLocation, Snooze, SnoozeNoteAttachment } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { InterventionSnoozeData } from "modules/AppointmentDetails/components/Interventions/components";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { ModalProps } from "semantic-ui-react";

export interface SnoozeItemWithHistory extends Snooze {
  history: Snooze[];
}

//TODO: Define one type for incoming data, get rid of these union types, it's hellish to work with,
// Need a separate ticket for that, as this is a big refactor
interface SnoozeModalProps extends ModalProps {
  data: QuestionResultData | AddInterventionPayload | InterventionSnoozeData | Snooze;
  dealer?: Dealer | null;
  location?: DealerLocation | null;
  isDeleteSnoozeLoading?: boolean;
  isAppendSnoozeLoading?: boolean;
  onClose?: () => void;
  onSave?: (data: Snooze) => void;
  onAppendSnooze?: (snooze: Snooze) => Promise<void>;
  onDeleteSnooze?: (snooze: DeleteSnoozePayload) => Promise<void>;
}

export type DeleteSnoozePayload = {
  question_result_id: number | null;
  intervention_id: number | null;
  note: string;
};

const FormHelper = createFormHelper<SnoozeFormValues>();

//TODO: Refactor component, it's a mess, move to RHF
export const SnoozeModal = ({
  open,
  onClose,
  data,
  dealer,
  location,
  onSave,
  onAppendSnooze,
  onDeleteSnooze,
  isDeleteSnoozeLoading,
  isAppendSnoozeLoading
}: SnoozeModalProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { appointment } = useAppointmentForSnoozeQuery(Number(data.appointment_id));

  const [snoozeItem, setSnoozeItem] = useState<Snooze | null>(null);
  const [attachments, setAttachments] = useState<SnoozeNoteAttachment[]>([]);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [deleteConfirmationNote, setDeleteConfirmationNote] = useState<string>("");

  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/snoozes/note_attachment/upload", ENV.appointmentBaseURL);

  useEffect(() => {
    if ("snooze_history" in data) {
      const sortedSnoozeHistory = data.snooze_history?.sort((history1, history2) => history2.created_on.localeCompare(history1.created_on)) ?? [];
      const latestSnoozeItem = sortedSnoozeHistory.length > 0 && sortedSnoozeHistory[0].snooze_status_id !== SNOOZE_STATUSES.Deleted ? sortedSnoozeHistory[0] : null;
      setSnoozeItem(latestSnoozeItem);
    }

    if ("attachments" in data && data.attachments?.length) setAttachments(data.attachments.map(a => ({ snooze_id: 0, ...a, id: "id" in a ? a.id : 0 })));
  }, [data]);

  const handleUploadAttachment = async (file: File) => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });
    const formData = new FormData();

    const date = appointment?.created_on && formatDate(appointment.created_on, DATE_FORMATS_FNS.yearMonthDateSlash);
    const path = `${location?.id}_${location?.name}/${date}/${appointment?.id}_${appointment?.wo_nr}`;

    formData.append("file", uploadFile);
    formData.append("path", path);

    const attachmentUrl = await handleUploadFileAttachment({ formData });

    if (attachmentUrl) {
      const attachment = { url: attachmentUrl, created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat), id: -1, snooze_id: snoozeItem?.id || -1 };
      setAttachments(attachments => attachments.concat(attachment));
    }
  };

  const handleDeleteAttachment = (url: string) => {
    setAttachments(attach => attach.filter(attachment => attachment.url !== url));
  };

  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = getFilesWithinSizeLimit(Array.from(evt.target.files ?? []), t);

    files.forEach(file => handleUploadAttachment(file));
  };

  const handleFileDragOver = (evt: React.DragEvent<HTMLInputElement>) => evt.preventDefault();

  const handleFileDrop = (evt: React.DragEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const files = getFilesWithinSizeLimit(Array.from(evt.dataTransfer.files), t);

    files.forEach(file => handleUploadAttachment(file));
  };

  const constructNewSnoozeReqPayload = (formData: SnoozeFormValues): Snooze | null => {
    if (!data.appointment_id || !formData.snooze_type_id || (dealer?.is_snooze_status_enabled && !formData.snooze_status_id)) {
      setHasValidationError(true);
      return null;
    }

    const addSnoozePayload: Snooze = {
      id: -1,
      car_id: "car_id" in data ? data.car_id || -1 : -1,
      updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      dealer_location_id: location?.id || 0,
      appointment_id: data.appointment_id,
      note: formData.note || "",
      snooze_type_id: formData.snooze_type_id,
      snooze_status_id: formData.snooze_status_id || null,
      snooze_department_id: formData.snooze_department_id || null,
      snooze_date: formData.snooze_date ? format(subMinutes(formData.snooze_date, formData.snooze_date.getTimezoneOffset()), DATE_FORMATS_FNS.ISOFormat) : null,
      created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      user_id: user?.id || 0,
      question_result_id: data.question_result_id || null,
      intervention_id: "intervention_id" in data && data.intervention_id ? data.intervention_id : null,
      intervention: "intervention" in data ? data.intervention : undefined,
      appointment: "appointment" in data ? data.appointment : undefined,
      user,
      car: "car" in data ? data.car : undefined,
      question_result: "question_result" in data ? data.question_result : undefined
    };
    if (attachments.length) addSnoozePayload.note_attachments = attachments.map(attachment => attachment.url);

    return addSnoozePayload;
  };

  const handleSubmitForm = async (formData: SnoozeFormValues) => {
    const newSnooze = constructNewSnoozeReqPayload(formData);
    if (!newSnooze) return;

    await onAppendSnooze?.(newSnooze);
    onSave?.(newSnooze);
    onClose?.();
  };

  const handleDeleteSnooze = async () => {
    if (!deleteConfirmationNote) return;
    await onDeleteSnooze?.({
      intervention_id: "intervention_id" in data ? data.intervention_id || null : null,
      question_result_id: data.question_result_id || null,
      note: deleteConfirmationNote
    });

    setShowDeleteConfirmation(false);
    onClose?.();
  };

  return (
    <Modal size="large" open={open} onClose={onClose} closeOnDimmerClick className="InterventionModal SnoozeModal">
      <Modal.Header>
        <div className="notes-modal-header-icon">
          <Icon className="screwdriver wrench" />
        </div>
        <p className="snooze-item-title">{t("v8_snooze_item", "Snooze Item")}</p>
      </Modal.Header>
      <Modal.Content>
        <FormHelper.Form formProps={{ id: "new-intervention" }} onSubmit={handleSubmitForm}>
          <SnoozeForm latestSnoozeItem={snoozeItem} dealer={dealer} hasValidationError={hasValidationError} />
        </FormHelper.Form>

        <LogHistory snoozeHistory={"snooze_history" in data ? data.snooze_history : []} dealer={dealer} />
        {dealer?.is_snooze_attachment_enabled && (
          <>
            <UploadFilesZone
              display={true}
              isUploading={isUploadingAttachment}
              onFileInputChange={handleFileChange}
              onFileDragOver={handleFileDragOver}
              onFileDrop={handleFileDrop}
              hasError={false}
            />
            <UploadedFilesPreview
              attachments={[...("images" in data && data.images?.length ? data.images : []), ...attachments]}
              onDeleteAttachment={handleDeleteAttachment}
            />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <div className={`${snoozeItem?.snooze_type_id ? "update-snooze-modal-actions" : "add-snooze-modal-actions"}`}>
          <div>
            {snoozeItem?.snooze_type_id && (
              <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.Snooze}>
                <Button onClick={() => setShowDeleteConfirmation(true)} color="red">
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Button>
              </Can>
            )}
          </div>
          <div>
            <Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button color="green" form="new-intervention" type="submit" disabled={isAppendSnoozeLoading || isUploadingAttachment} loading={isAppendSnoozeLoading}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </div>
      </Modal.Actions>
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteConfirmation}
        customClass={"CancelAppointmentModal"}
        handleCancel={() => setShowDeleteConfirmation(false)}
        note={deleteConfirmationNote}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        onChangeNote={e => setDeleteConfirmationNote(e.target.value)}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteSnooze}
        isLoading={isDeleteSnoozeLoading}
        confirmMandatoryNote={t("v8_reason_for_deleting_snoozed_item", "Reason for deleting snoozed item.")}
      />
    </Modal>
  );
};
